import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import "./App.css";
import AppNavbar from "./AppNavbar.js";
import SectionCard from "./SectionCard.js";
import { communityFetch } from "./communityApi.js";
import {
  buildLoginUrl,
  buildLogoutUrl,
  buildSignupUrl,
  getTokenStorageKey,
  isTokenExpired,
  readTokenFromUrlHash,
} from "./auth.js";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_REDIRECT_URI = `${window.location.origin}/communities/new`;
const COGNITO_LOGOUT_URI = `${window.location.origin}/communities`;

function CreateCommunityPage() {
  const navigate = useNavigate();
  const [idToken, setIdToken] = useState("");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState({ cash_available: 0, cash_reserved: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [formState, setFormState] = useState({
    name: "",
    description: "",
    visibility: "PUBLIC",
    password: "",
  });

  useEffect(() => {
    const tokenFromHash = readTokenFromUrlHash();
    const storedToken = localStorage.getItem(getTokenStorageKey()) || "";
    const token = tokenFromHash || storedToken;

    if (!token || isTokenExpired(token)) {
      localStorage.removeItem(getTokenStorageKey());
      setIdToken("");
      return;
    }

    localStorage.setItem(getTokenStorageKey(), token);
    setIdToken(token);
    if (tokenFromHash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loginUrl = useMemo(
    () => buildLoginUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const signupUrl = useMemo(
    () => buildSignupUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const logoutUrl = useMemo(
    () => buildLogoutUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_LOGOUT_URI),
    []
  );

  const authFetch = useCallback(async (path) => {
    if (!API_BASE_URL) {
      throw new Error("Missing REACT_APP_API_BASE_URL");
    }
    if (!idToken) {
      throw new Error("Not signed in");
    }
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    });
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body.error || body.message || "Request failed");
    }
    return body;
  }, [idToken]);

  useEffect(() => {
    let cancelled = false;
    const loadAccountHeader = async () => {
      if (!idToken) {
        setMe(null);
        setBalance({ cash_available: 0, cash_reserved: 0 });
        setError("");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const [meRes, balanceRes] = await Promise.all([
          authFetch("/me"),
          authFetch("/me/balance"),
        ]);
        if (!cancelled) {
          setMe(meRes);
          setBalance(balanceRes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load account");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadAccountHeader();
    return () => {
      cancelled = true;
    };
  }, [idToken, authFetch]);

  const handleLogin = () => {
    if (!loginUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(loginUrl);
  };

  const handleSignup = () => {
    if (!signupUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(signupUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem(getTokenStorageKey());
    setIdToken("");
    setMe(null);
    setBalance({ cash_available: 0, cash_reserved: 0 });
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateCommunity = async (event) => {
    event.preventDefault();
    setSubmitError("");

    const name = formState.name.trim();
    if (!name) {
      setSubmitError("name is required");
      return;
    }
    if (formState.visibility === "PRIVATE" && !formState.password.trim()) {
      setSubmitError("password is required for private communities");
      return;
    }

    const payload = {
      name,
      visibility: formState.visibility,
    };
    const description = formState.description.trim();
    if (description) {
      payload.description = description;
    }
    if (formState.visibility === "PRIVATE") {
      payload.password = formState.password.trim();
    }

    setSubmitting(true);
    try {
      const response = await communityFetch("/communities", idToken, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const communityId = response?.item?.community_id;
      if (communityId) {
        navigate(`/communities/${communityId}`);
        return;
      }
      navigate("/communities");
    } catch (err) {
      setSubmitError(err.message || "Failed to create community");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App">
      <AppNavbar
        links={[
          { href: "/", label: "Home" },
          { href: "/open-orders", label: "Open Orders" },
          { href: "/communities", label: "Communities" },
          { href: "/leaderboard", label: "Leaderboard" },
          { href: "/market-resolution", label: "Resolve Market" },
          { href: "/markets/new", label: "Create Market" },
        ]}
        isSignedIn={Boolean(idToken)}
        me={me}
        balance={balance}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onSignup={handleSignup}
      />
      <Container className="py-4">
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" className="mb-0">{error}</Alert>
            </Col>
          </Row>
        )}
        {!idToken ? (
          <Row>
            <Col>
              <Alert variant="info" className="mb-0">
                Sign in with Cognito to create communities.
              </Alert>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col lg={{ span: 8, offset: 2 }}>
              <SectionCard title="Create Community">
                {submitError && <Alert variant="danger">{submitError}</Alert>}
                <Form onSubmit={handleCreateCommunity}>
                  <Form.Group className="mb-3 text-start">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      name="name"
                      value={formState.name}
                      onChange={handleInputChange}
                      disabled={submitting}
                      placeholder="Community name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 text-start">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formState.description}
                      onChange={handleInputChange}
                      disabled={submitting}
                      placeholder="What is this community for?"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 text-start">
                    <Form.Label>Visibility</Form.Label>
                    <Form.Select
                      name="visibility"
                      value={formState.visibility}
                      onChange={handleInputChange}
                      disabled={submitting}
                    >
                      <option value="PUBLIC">Public</option>
                      <option value="PRIVATE">Private</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Private communities are visible to everyone, but require a password to request membership.
                    </Form.Text>
                  </Form.Group>

                  {formState.visibility === "PRIVATE" && (
                    <Form.Group className="mb-3 text-start">
                      <Form.Label>Join Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={formState.password}
                        onChange={handleInputChange}
                        disabled={submitting}
                        placeholder="Required for private communities"
                      />
                    </Form.Group>
                  )}

                  <div className="community-create-actions">
                    <Button variant="outline-light" href="/communities" disabled={submitting || loading}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting || loading}>
                      {submitting ? "Creating..." : "Create Community"}
                    </Button>
                  </div>
                </Form>
              </SectionCard>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}

export default CreateCommunityPage;
